import { useMutation } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'

import type { ASOFilterState } from './Filters'
import type { CellClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, SyntheticEvent } from 'react'
import type { IASOAdvancedKeyword } from '~/types/aso'

import columns from './columns'
import CustomHeader from './customHeader'
import ASOFilters, { initialState } from './Filters'

import { markAsBrand } from '~/api/application-aso'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ServerSideTable from '~/components/Tables/ServerSideTable'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTablePagination } from '~/hooks/useTablePagination'
import { useAppSummary } from '~/providers/AppSummary'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { useASOAdvanced } from '~/queries/aso'
import { transformByDeviceType } from '~/utils/application'
import { handleGenericError } from '~/utils/error'
import { debounce } from '~/utils/function'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'asoKeywords'

const ASOAdvanced = (): ReactElement => {
  /** Local state */

  const { appDetails, appKey, deviceType } = useAppSummary()

  const { searchParamsObject } = useSearchParamsContext()

  const country = searchParamsObject.country

  const gridRef = useRef<AgGridReact<IASOAdvancedKeyword>>(null)

  const [filters, setFilters] = useState<ASOFilterState>(initialState(country))

  const [search, setSearch] = useState('')

  const paginationApi = useTablePagination(ENTRIES_NAME)

  /** Queries & Mutations */

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  const { mutateAsync: markAsBrandMutation } = useMutation({ mutationFn: markAsBrand })

  const { data: asoData } = useASOAdvanced({
    appId: appSummary?.app_id,
    filters: {
      cluster_id: appSummary?.default_cluster,
      country: filters.country,
      quality: filters.rating.toString(),
    },
  })

  /** Effects */

  useDocumentTitle('ASO Advanced')

  /** Handlers */

  const handleFilterChange = useCallback((filters: ASOFilterState) => {
    setFilters(filters)

    if (gridRef.current?.api) {
      gridRef.current.api.setColumnsVisible(
        ['avg_sov', 'avg_ttr', 'avg_cr', 'avg_install_rate'],
        filters.displayASAColumn
      )
      gridRef.current.api.setColumnsVisible(['keyword_quality'], filters.rating === -1)
    }
  }, [])

  const handleMarkAsBrand = (keyword: string, markAsBrand: boolean) => {
    try {
      markAsBrandMutation({ brand: keyword, markAsBrand, owned: true })
      displayToast(markAsBrand ? 'Marked as brand' : 'Removed brand tag', 'success')
    } catch (e) {
      handleGenericError(e)
    }
  }

  const handleCellClick = (event: CellClickedEvent) => {
    if (event.column.getColId() !== 'keyword') return

    navigator.clipboard.writeText(event.data.keyword)
    displayToast('Copied to clipboard', 'success')
  }

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const handleSearchChange = (e: SyntheticEvent<HTMLInputElement>) => debouncedSearch(e.currentTarget.value)

  /** Render */

  const filteredData = asoData?.keywords.filter(
    ({ competitiveness_score, is_asa_keyword, keyword, popularity }) =>
      competitiveness_score >= filters.competitiveness.min &&
      competitiveness_score <= filters.competitiveness.max &&
      popularity * 100 >= filters.popularity.min &&
      popularity * 100 <= filters.popularity.max &&
      ((filters.onlyASAKeywords && is_asa_keyword) || !filters.onlyASAKeywords) &&
      (!search || keyword.includes(search))
  )

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="aso-advanced-view" withPadding>
        <ViewTitle className="layout-view-title">ASO advanced</ViewTitle>
        <div className="aso-advanced-view__content">
          <ASOFilters
            clusterRankingViewUrl="/competitors/clusters"
            country={country}
            metadata={asoData?.metadata}
            onFilterChange={handleFilterChange}
          />
          <ContentLayout>
            <Section className="section">
              <div className="keyword-count">
                <span className="keyword-count__counter">{filteredData?.length || 0}</span> items
              </div>
              <ServerSideTable
                className="aso-listing-table"
                columnDefs={columns}
                components={{ customHeader: CustomHeader }}
                context={{
                  handleMarkAsBrand: handleMarkAsBrand,
                  metadata: asoData?.metadata || [],
                }}
                currentPage={0}
                entriesName={ENTRIES_NAME}
                onCellClicked={handleCellClick}
                onSearchChange={handleSearchChange}
                paginationApi={paginationApi}
                ref={gridRef}
                rowData={filteredData}
                rowStyle={{ cursor: 'default' }}
                total={asoData?.keywords.length || 0}
                withPagination={false}
                withSearchBar
              />
            </Section>
          </ContentLayout>
        </div>
      </Layout>
    </>
  )
}

export default ASOAdvanced
